import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import * as S from '../styles';
import Texts from '../../Texts';
import { toast } from 'react-toastify';
import { ApplicationFormData } from './types';

interface PropsApplicationEditStepThree {
  en_description: string;
  fa_description: string;
  subtitle_fa?: string;
  subtitle_en?: string;
  onFieldChange: <K extends keyof ApplicationFormData>(
    field: K,
    value: ApplicationFormData[K],
  ) => void;
  publisher_id?: string;
  application_id?: string;
}

const ApplicationEditStepThree = forwardRef(
  (
    {
      en_description,
      fa_description,
      subtitle_fa = '',
      subtitle_en = '',
      onFieldChange,
      publisher_id,
      application_id,
    }: PropsApplicationEditStepThree,
    ref,
  ) => {
    const [localEnDescription, setLocalEnDescription] =
      useState(en_description);
    const [localFaDescription, setLocalFaDescription] =
      useState(fa_description);
    const [localSubtitleFa, setLocalSubtitleFa] = useState(subtitle_fa);
    const [localSubtitleEn, setLocalSubtitleEn] = useState(subtitle_en);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
      setLocalEnDescription(en_description);
      setLocalFaDescription(fa_description);
      setLocalSubtitleFa(subtitle_fa);
      setLocalSubtitleEn(subtitle_en);
    }, [en_description, fa_description, subtitle_fa, subtitle_en]);

    const handleEnDescriptionChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      const value = event.target.value;
      setLocalEnDescription(value);
      onFieldChange('en_description', value);
    };

    const handleFaDescriptionChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      const value = event.target.value;
      setLocalFaDescription(value);
      onFieldChange('fa_description', value);
    };

    const handleSubtitleFaChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      const value = event.target.value;
      setLocalSubtitleFa(value);
      onFieldChange('subtitle_fa', value);
    };

    const handleSubtitleEnChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      const value = event.target.value;
      setLocalSubtitleEn(value);
      onFieldChange('subtitle_en', value);
    };

    const handleSubmit = () => {
      onFieldChange('en_description', localEnDescription);
      onFieldChange('fa_description', localFaDescription);
      onFieldChange('subtitle_fa', localSubtitleFa);
      onFieldChange('subtitle_en', localSubtitleEn);
    };

    useImperativeHandle(ref, () => ({
      submit: handleSubmit,
      getValues: () => ({
        en_description: localEnDescription,
        fa_description: localFaDescription,
        subtitle_fa: localSubtitleFa,
        subtitle_en: localSubtitleEn,
      }),
    }));

    const handleModalClick = () => {
      setIsModalOpen(true);
    };

    const handleCloseModal = () => {
      setIsModalOpen(false);
    };

    return (
      // Full-width row on the edit page, so no 520px cap.
      <S.Container fluid>
        {/* فیلدهای subtitle در یک سطر */}
        <S.DiscriptionBox>
          <S.DescriptionFieldWrapper>
            <S.Label>زیرعنوان انگلیسی</S.Label>
            <S.TextFieldWrapper>
              <S.TextFieldBackground />
              <S.STextField
                id="subtitle_en"
                name="subtitle_en"
                type="text"
                placeholder="English Subtitle"
                value={localSubtitleEn}
                onChange={handleSubtitleEnChange}
                dir="ltr"
              />
            </S.TextFieldWrapper>
          </S.DescriptionFieldWrapper>

          <S.DescriptionFieldWrapper>
            <S.Label>زیرعنوان فارسی</S.Label>
            <S.TextFieldWrapper>
              <S.TextFieldBackground />
              <S.STextField
                id="subtitle_fa"
                name="subtitle_fa"
                type="text"
                placeholder="زیرعنوان فارسی"
                value={localSubtitleFa}
                onChange={handleSubtitleFaChange}
                dir="rtl"
              />
            </S.TextFieldWrapper>
          </S.DescriptionFieldWrapper>
        </S.DiscriptionBox>

        <S.DiscriptionBox>
          <S.DescriptionFieldWrapper>
            <S.Label>{Texts.ApplicationStepThree.lableDescriptionEn}</S.Label>
            <S.DescriptionFieldLeft
              multiline
              id="en_description"
              name="en_description"
              type="text"
              placeholder={Texts.ApplicationStepThree.placeHolderEn}
              value={localEnDescription}
              onChange={handleEnDescriptionChange}
              inputProps={{ maxLength: 10000 }}
            />
            <div
              style={{
                fontSize: '12px',
                color: localEnDescription.length > 9000 ? '#f44336' : '#666',
                textAlign: 'left',
                marginTop: '4px',
              }}
            >
              {localEnDescription.length}/10000 کاراکتر
            </div>
          </S.DescriptionFieldWrapper>
          <S.DescriptionFieldWrapper>
            <S.Label>{Texts.ApplicationStepThree.lableDescriptionFa}</S.Label>
            <S.DescriptionFieldRight
              multiline
              id="fa_description"
              name="fa_description"
              type="text"
              placeholder={Texts.ApplicationStepThree.placeHolderFa}
              value={localFaDescription}
              onChange={handleFaDescriptionChange}
              inputProps={{ maxLength: 10000 }}
            />
            <div
              style={{
                fontSize: '12px',
                color: localFaDescription.length > 9000 ? '#f44336' : '#666',
                textAlign: 'right',
                marginTop: '4px',
              }}
            >
              {localFaDescription.length}/10000 کاراکتر
            </div>
          </S.DescriptionFieldWrapper>
        </S.DiscriptionBox>
      </S.Container>
    );
  },
);

export default ApplicationEditStepThree;
